import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';

// Every maintenance-overview confirmation shares this decline label.
const declineText = (transloco: TranslocoService): string =>
  transloco.translate('maintenanceOverview.confirmations.decline');

// NOTE: These factories always return a new ConfirmationOptions object. The modal writes back to
// the mutable `result` field, so callers must never share a single instance.

export function createApproveMaintenanceRequestConfirmation(
  transloco: TranslocoService,
  onConfirm: () => Promise<any>
): ConfirmationOptions {
  return {
    title: transloco.translate('maintenanceOverview.confirmations.approveRequest.title'),
    message: transloco.translate('maintenanceOverview.confirmations.approveRequest.message'),
    warningMessage: transloco.translate('maintenanceOverview.confirmations.approveRequest.warning'),
    confirmText: transloco.translate('maintenanceOverview.confirmations.approveRequest.confirm'),
    declineText: declineText(transloco),
    onConfirm
  };
}

export function createRejectMaintenanceRequestConfirmation(
  transloco: TranslocoService,
  onConfirm: () => Promise<any>
): ConfirmationOptions {
  return {
    title: transloco.translate('maintenanceOverview.confirmations.rejectRequest.title'),
    message: transloco.translate('maintenanceOverview.confirmations.rejectRequest.message'),
    warningMessage: transloco.translate('maintenanceOverview.confirmations.rejectRequest.warning'),
    confirmText: transloco.translate('maintenanceOverview.confirmations.rejectRequest.confirm'),
    declineText: declineText(transloco),
    onConfirm
  };
}

export function createResetMaintenanceRequestConfirmation(
  transloco: TranslocoService,
  onConfirm: () => Promise<any>
): ConfirmationOptions {
  return {
    title: transloco.translate('maintenanceOverview.confirmations.resetRequest.title'),
    message: transloco.translate('maintenanceOverview.confirmations.resetRequest.message'),
    warningMessage: transloco.translate('maintenanceOverview.confirmations.resetRequest.warning'),
    confirmText: transloco.translate('maintenanceOverview.confirmations.resetRequest.confirm'),
    declineText: declineText(transloco),
    onConfirm
  };
}

// Bulk approve intentionally has no onConfirm - the caller acts on the resolved confirmation instead.
export function createBulkApproveMaintenanceRequestsConfirmation(
  transloco: TranslocoService,
  count: number
): ConfirmationOptions {
  return {
    title: transloco.translate('maintenanceOverview.confirmations.bulkApprove.title'),
    message: transloco.translate(
      count > 1
        ? 'maintenanceOverview.confirmations.bulkApprove.messageMany'
        : 'maintenanceOverview.confirmations.bulkApprove.messageOne',
      { count }
    ),
    warningMessage: transloco.translate('maintenanceOverview.confirmations.bulkApprove.warning'),
    confirmText: transloco.translate('maintenanceOverview.confirmations.bulkApprove.confirm'),
    declineText: declineText(transloco)
  };
}

// confirmPlaceholder and confirmationPrompt drive the type-to-confirm gate on this destructive action.
export function createDeleteMaintenanceConfirmation(
  transloco: TranslocoService,
  onConfirm: () => Promise<any>
): ConfirmationOptions {
  return {
    title: transloco.translate('maintenanceOverview.confirmations.deleteMaintenance.title'),
    message: transloco.translate('maintenanceOverview.confirmations.deleteMaintenance.message'),
    warningMessage: transloco.translate('maintenanceOverview.confirmations.deleteMaintenance.warning'),
    confirmText: transloco.translate('maintenanceOverview.confirmations.deleteMaintenance.confirm'),
    declineText: declineText(transloco),
    confirmPlaceholder: transloco.translate('maintenanceOverview.confirmations.deleteMaintenance.confirmPlaceholder'),
    confirmationPrompt: 'delete',
    onConfirm
  };
}
